import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

const PROJECT_LOGOS = {
  'ד החדשה': '/logos/jaffad.png',
  'אייזיקחריף': '/logos/aizik_harif.png',
  'ג החדשה': '/logos/jaffag.png',
  'סקייליין בת ים': '/logos/aura_logo.jpg',
  'ראשון החדשה': '/logos/rishon.jpg',
  'אפלטון 6': '/logos/aplaton6.jpeg',
  'שארית ישראל': '/logos/sherit_israel.jpg',
};

const CLIENT_LOGOS = {
  'ד החדשה':      '/logos/jaffad.png',
  'סקייליין בת ים': '/logos/community_group_logo.png',
};

// "ג החדשה" is the literal project_name in the DB (used for API calls and
// matching) — this only changes what's shown to the user.
const PROJECT_DISPLAY_NAMES = { 'ג החדשה': "ג' החדשה", 'אייזיקחריף': 'אייזיק חריף' };
const displayProjectName = n => PROJECT_DISPLAY_NAMES[n] || n;

const AURA_PROJECTS = new Set(['ד החדשה', 'סקייליין בת ים']);
const GAVSO_PROJECTS = new Set(['ג החדשה', 'אייזיקחריף']);

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

// Shared draggable floating AI button — same pointer-drag pattern as the
// tenant portal's own AI button, so it can be nudged aside on a phone
// browser instead of sitting fixed over content. Used by all three hub
// layouts below (Aura, Gavso, default) under a shared localStorage key.
function FloatingAIButton({ projectName, navigate }) {
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hubAiChatBtnPos')) || { bottom: 28, left: 28 }; }
    catch { return { bottom: 28, left: 28 }; }
  });
  const dragRef = useRef(null);
  const wasDragRef = useRef(false);
  const btnRef = useRef(null);

  function onPointerDown(e) {
    dragRef.current = { startX: e.clientX, startY: e.clientY, startBottom: pos.bottom, startLeft: pos.left, moved: false, curLeft: pos.left, curBottom: pos.bottom };
    e.currentTarget.setPointerCapture(e.pointerId);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }
  function onPointerMove(e) {
    if (!dragRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragRef.current.moved = true;
    if (!dragRef.current.moved) return;
    const newLeft = Math.max(4, Math.min(window.innerWidth - 60, dragRef.current.startLeft + dx));
    const newBottom = Math.max(4, Math.min(window.innerHeight - 60, dragRef.current.startBottom - dy));
    dragRef.current.curLeft = newLeft;
    dragRef.current.curBottom = newBottom;
    if (btnRef.current) {
      btnRef.current.style.left = `${newLeft}px`;
      btnRef.current.style.bottom = `${newBottom}px`;
    }
  }
  function onPointerUp() {
    if (!dragRef.current) return;
    wasDragRef.current = !!dragRef.current.moved;
    if (wasDragRef.current) {
      const p = { left: dragRef.current.curLeft, bottom: dragRef.current.curBottom };
      localStorage.setItem('hubAiChatBtnPos', JSON.stringify(p));
      setPos(p);
    }
    dragRef.current = null;
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }

  return (
    <button
      ref={btnRef}
      onClick={() => { if (!wasDragRef.current) navigate(`/admin/${projectName}?tab=ai-chat`); }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      title="עוזר AI"
      style={{
        position: 'fixed', bottom: pos.bottom, left: pos.left, zIndex: 9999,
        background: 'white', border: 'none', padding: 0, overflow: 'hidden',
        borderRadius: '50%', width: 56, height: 56, fontSize: '1.6rem',
        cursor: 'pointer', boxShadow: 'none', touchAction: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'transform 0.15s',
      }}
      onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
      onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <img
        src="https://aura-tenant-portal-files.s3.us-east-1.amazonaws.com/home-images/1788079441278-iakp5i51gfe.png"
        alt=""
        draggable={false}
        onDragStart={e => e.preventDefault()}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', pointerEvents: 'none' }}
      />
    </button>
  );
}

function getWorkerRole() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'admin';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || 'admin';
  } catch { return 'admin'; }
}

// Digital-business-card trial tile — visible only to this one account until
// the feature is reviewed and rolled out to everyone (see the plan doc).
const TRIAL_USERNAMES = new Set(['eladp', 'or', 'shay']);
function isTrialUser() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return TRIAL_USERNAMES.has(payload.userName);
  } catch { return false; }
}

// ── Default (non-Aura) components ────────────────────────────────────────────

function StatBox({ label, value, color = '#1a4a8a', sub, flex }) {
  return (
    <div style={{
      background: 'white', borderRadius: 10, padding: '14px 12px',
      border: `2px solid ${color}`, textAlign: 'center',
      flex: flex || undefined, display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value ?? '—'}</div>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Aura-styled components ────────────────────────────────────────────────────

function AuraStatTile({ value, label, border = '#d7dae3', numColor = '#181f36', sub }) {
  return (
    <div style={{ border: `1.5px solid ${border}`, borderRadius: 14, padding: 12, textAlign: 'center', background: '#fff' }}>
      <div style={{ font: `800 22px 'Assistant',sans-serif`, color: numColor }}>{value ?? '—'}</div>
      <div style={{ font: `500 12px 'Assistant',sans-serif`, color: '#6b7280', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ font: `400 11px 'Assistant',sans-serif`, color: '#9aa0af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function AuraTile({ emoji, label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      style={{
        background: '#fff', border: 'none', borderRadius: 16,
        padding: '22px 14px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 10, cursor: 'pointer',
        boxShadow: hovered ? '0 6px 20px rgba(24,31,54,.13)' : '0 2px 10px rgba(24,31,54,.06)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow 0.15s, transform 0.15s',
        font: `600 13px 'Assistant',sans-serif`, color: '#181f36',
      }}
    >
      <span style={{ fontSize: '1.6rem' }}>{emoji}</span>
      <span>{label}</span>
    </button>
  );
}

// ── Aura Hub page (v2 — gold/cream/dark theme) ────────────────────────────────

const G = '#c9973f';      // gold accent
const TILE_BG = '#F1E3CA';
const TILE_ICON_BG = '#f7e9cd';
const DARK_LEFT = '#171D2D';
const DARK_RIGHT = '#1E2A49';
const DARK_HEADING = '#E4C47D';
const STAT_TILE_BG = '#FAF6EE';
const F = "'Assistant', Arial, sans-serif";

function AuraGoldTile({ emoji, label, onClick, tileBg = TILE_BG, iconBg = TILE_ICON_BG, accent = G, shadowRgb = '201,151,63', border }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseOver={() => setHov(true)} onMouseOut={() => setHov(false)}
      style={{ background: tileBg, border: border ? `1px solid ${border}` : 'none', borderRadius: 18, padding: '28px 14px',
        textAlign: 'center', height: 130,
        boxShadow: hov ? `0 6px 20px rgba(${shadowRgb},.22)` : '0 2px 10px rgba(24,31,54,.06)',
        transform: hov ? 'translateY(-2px)' : 'none', transition: 'box-shadow .15s, transform .15s',
        cursor: 'pointer', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <div style={{ width: 50, height: 50, borderRadius: 14, background: iconBg, color: accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
        {emoji}
      </div>
      <div style={{ font: `600 14px ${F}`, color: '#181f36' }}>{label}</div>
    </button>
  );
}

function AuraGoldStat({ value, label, border = '#d7dae3', numColor = '#181f36', sub, flex }) {
  return (
    <div style={{ border: `1.5px solid ${border}`, borderRadius: 14, padding: 12, textAlign: 'center', background: STAT_TILE_BG, flex: flex || undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ font: `800 22px ${F}`, color: numColor }}>{value ?? '—'}</div>
      <div style={{ font: `500 12px ${F}`, color: '#6b7280', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ font: `400 11px ${F}`, color: '#9aa0af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ד' החדשה's tenant portal already uses a navy theme — the hub tiles matched
// Skyline's gold theme regardless of project. Give ד' החדשה its own blue tiles.
const IS_BLUE_HUB = name => name === 'ד החדשה';
// Sampled directly from the ד' החדשה logo's ring (dominant pixel color).
const BLUE_TILE_BG = '#ffffff';
const BLUE_TILE_ICON_BG = '#e5e8f9';
const BLUE_ACCENT = '#000D91';

function AuraHub({ name, projectName, stats, navigate, MsgBadge }) {
  const isMobile = useIsMobile();
  const blueTheme = IS_BLUE_HUB(name);
  const tileProps = blueTheme
    ? { tileBg: BLUE_TILE_BG, iconBg: BLUE_TILE_ICON_BG, accent: BLUE_ACCENT, shadowRgb: '0,13,145', border: '#dfe3f0' }
    : {};
  const pct = stats ? parseFloat(stats.percentage) : null;
  const pctColor = pct === null ? '#6b7280' : pct >= 66.67 ? '#1e9e57' : '#dc2626';
  const pctBorder = pct === null ? '#d7dae3' : pct >= 66.67 ? '#bfe8cc' : '#f3c6c6';
  const pctSub = pct !== null ? (pct >= 66.67 ? 'עומד ביעד' : `חסר ${(66.67 - pct).toFixed(1)}%`) : '';

  const isAdmin = getWorkerRole() === 'admin';
  const WORK_TILES = [
    { label: 'ניהול בניינים', emoji: '🏢', path: `/buildings/${projectName}` },
    { label: 'דוחות',         emoji: '🗃️', path: `/reports/${projectName}` },
    { label: 'דשבורד',        emoji: '📊', path: `/dashboard/${projectName}` },
    { label: 'כנסים',          emoji: '🎉', path: `/events/${projectName}` },
    ...(decodeURIComponent(projectName) === 'ג החדשה' ? [{ label: 'שיבוצים', emoji: '🏢', path: `/placements/${projectName}` }] : []),
    ...(isTrialUser() ? [{ label: 'כרטיס ביקור דיגיטלי', emoji: '📇', path: `/card-editor/${projectName}` }] : []),
  ];
  const TENANT_TILES = [
    { label: 'דשבורד פעילות', emoji: '📊', path: `/admin/${projectName}?tab=dashboard` },
    { label: 'דף הבית',       emoji: '🏠', path: `/admin/${projectName}?tab=home` },
    { label: 'הדירה שלי',     emoji: '🏡', path: `/admin/${projectName}?tab=apartment` },
    { label: 'שלבים',         emoji: '📋', path: `/admin/${projectName}` },
    { label: 'הודעות',        emoji: '📢', path: `/admin/${projectName}?tab=announcements` },
    { label: 'גלריית תמונות', emoji: '📷', path: `/admin/${projectName}?tab=gallery` },
    { label: 'אנשי קשר',     emoji: '📞', path: `/admin/${projectName}?tab=contacts` },
    { label: 'קבצים',         emoji: '📁', path: `/admin/${projectName}?tab=files` },
    { label: 'אודות היזם',    emoji: 'ℹ️',  path: `/admin/${projectName}?tab=about` },
    ...(isAdmin ? [{ label: 'מעקב צפייה בדייר', emoji: '🔍', path: `/admin/${projectName}?tab=tenant-lookup` }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#eef0f3', fontFamily: F, padding: isMobile ? '20px 12px 40px' : '28px 32px 50px', maxWidth: 1300, margin: '0 auto', boxSizing: 'border-box' }}>

      <FloatingAIButton projectName={projectName} navigate={navigate} />

      {/* Breadcrumb */}
      <div style={{ font: `500 13px ${F}`, color: '#6b6f80', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 14, cursor: 'pointer' }}
        onClick={() => navigate('/projects')}>
        חזרה לפרויקטים <span>←</span>
      </div>

      {/* Logo card */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 26 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: isMobile ? '16px 28px' : '20px 56px', boxShadow: '0 2px 10px rgba(24,31,54,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={CLIENT_LOGOS[name]} alt="לוגו" style={{ height: isMobile ? (blueTheme ? 70 : 44) : (blueTheme ? 100 : 60), maxWidth: '100%', objectFit: 'contain' }} onError={e => e.target.style.display='none'} />
        </div>
      </div>

      {/* 3-column grid on desktop, stacked single column on mobile */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr 260px', gap: isMobile ? 16 : 20, alignItems: 'stretch' }}>

        {/* DOM first (visual RIGHT in RTL on desktop; moved first on mobile too) — תמונת מצב */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: isMobile ? 0 : 34, height: isMobile ? 'auto' : 'calc(100% - 34px)', order: isMobile ? 1 : 0 }}>
          <div style={{ background: blueTheme ? BLUE_ACCENT : DARK_RIGHT, borderRadius: 18, padding: 18, boxShadow: '0 2px 10px rgba(24,31,54,.06)', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ font: `700 14px ${F}`, color: blueTheme ? '#fff' : DARK_HEADING, marginBottom: 12 }}>תמונת מצב 📍</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {stats ? (
                <>
                  <AuraGoldStat value={stats.totalBuildings} label="בניינים" flex={1} />
                  <AuraGoldStat value={stats.totalApartments} label="דירות" flex={1} />
                  <AuraGoldStat value={stats.totalTenants} label="בעלי זכויות (בשלמות או חלקי)" flex={1} />
                  <AuraGoldStat value={stats.publicHousingCount} label="יחידת דיור ציבורי" border="#f0dcae" numColor={G} flex={1} />
                </>
              ) : <div style={{ font: `400 12px ${F}`, color: '#9aa0af', textAlign: 'center', padding: 12 }}>טוען...</div>}
            </div>
          </div>
        </div>

        {/* CENTER — tiles */}
        <div style={{ order: isMobile ? 0 : 1 }}>
          {MsgBadge && <MsgBadge bg="#fffbeb" textColor="#92400e" borderColor={G} />}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, marginBottom: 10 }}>
            <div style={{ width: 2, height: 14, background: G }} />
            <div style={{ font: `700 13px ${F}`, color: '#181f36' }}>פורטל עבודה</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14 }}>
            {WORK_TILES.map(t => <AuraGoldTile key={t.label} emoji={t.emoji} label={t.label} onClick={() => navigate(t.path)} {...tileProps} />)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, margin: '20px 0 10px' }}>
            <div style={{ width: 2, height: 14, background: G }} />
            <div style={{ font: `700 13px ${F}`, color: '#181f36' }}>פורטל דיירים</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14 }}>
            {TENANT_TILES.map(t => <AuraGoldTile key={t.label} emoji={t.emoji} label={t.label} onClick={() => navigate(t.path)} {...tileProps} />)}
          </div>
        </div>

        {/* DOM last (visual LEFT in RTL on desktop; last on mobile too) — השלמות ומעקב */}
        <div style={{ background: blueTheme ? BLUE_ACCENT : DARK_LEFT, borderRadius: 18, padding: 18, boxShadow: '0 2px 10px rgba(24,31,54,.06)', marginTop: isMobile ? 0 : 34, height: isMobile ? 'auto' : 'calc(100% - 34px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', order: 2 }}>
          <div style={{ font: `700 14px ${F}`, color: blueTheme ? '#fff' : DARK_HEADING, marginBottom: 12 }}>השלמות ומעקב 🏗️</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            {stats ? (
              <>
                <AuraGoldStat value={stats.totalSigned != null ? Number(stats.totalSigned).toFixed(0) : '—'} label="תיעודים + מסמכים חתומים" border="#bfe8cc" numColor="#1e9e57" sub={`מתוך ${stats.totalApartments} דירות`} flex={1} />
                <AuraGoldStat value={pct !== null ? `${pct.toFixed(1)}%` : '—'} label="אחוז הסכמה" border={pctBorder} numColor={pctColor} sub={pctSub} flex={1} />
                {stats.overdueFollowUps > 0 && <AuraGoldStat value={stats.overdueFollowUps} label="מעקבים לטיפול" border="#f3c6c6" numColor="#dc2626" sub="תאריך מעקב שעבר" flex={1} />}
              </>
            ) : <div style={{ font: `400 12px ${F}`, color: '#9aa0af', textAlign: 'center', padding: 12 }}>טוען...</div>}
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Gavso Hub (design 1c — warm cream, gold gradient header) ─────────────────

const GV = '#8a6a2a';       // gold dark
const GV_LIGHT = '#d9b869'; // gold light
const GV_BG = '#f6efe0';    // warm cream background
const GV_TILE_BG = '#fff';
const GV_ICON_BG = '#f0e0b8';
const GV_BORDER = '#e9dcb8';
const GV_STAT_BORDER = '#e3d5ac';
const GV_TEXT = '#3d2f14';
const GV_F = "'Assistant', Arial, sans-serif";

function GavsoTile({ emoji, label, onClick, tileBg = GV_TILE_BG, iconBg = GV_ICON_BG, iconColor = GV }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseOver={() => setHov(true)} onMouseOut={() => setHov(false)}
      style={{
        background: tileBg, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 16,
        padding: '24px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
        cursor: 'pointer', width: '100%',
        boxShadow: hov ? '0 6px 18px rgba(0,0,0,.18)' : '0 1px 3px rgba(0,0,0,.08)',
        transform: hov ? 'translateY(-2px)' : 'none', transition: 'box-shadow .15s, transform .15s',
        font: `600 15px ${GV_F}`, color: '#2b2b2b',
      }}>
      <div style={{ width: 50, height: 50, borderRadius: 12, background: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>{emoji}</div>
      <span>{label}</span>
    </button>
  );
}

function GavsoStat({ value, label, numColor = GV, border = GV_STAT_BORDER, bg = '#fff', labelColor = '#6b5a3a', flex }) {
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 10px', textAlign: 'center', flex: flex || undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ font: `800 24px ${GV_F}`, color: numColor }}>{value ?? '—'}</div>
      <div style={{ font: `400 12px ${GV_F}`, color: labelColor, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function getWorkerName() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    return `${p.workerFirstName || ''} ${p.workerLastName || ''}`.trim();
  } catch { return ''; }
}

function isGavsoUser() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    const perms = (p.projectsPermission || '').split(',').map(s => s.trim()).filter(Boolean);
    return perms.length > 0 && perms.every(proj => GAVSO_PROJECTS.has(proj));
  } catch { return false; }
}

function GavsoHub({ name, projectName, stats, navigate, MsgBadge }) {
  const isMobile = useIsMobile();
  const pct = stats ? parseFloat(stats.percentage) : null;
  const pctColor = pct === null ? GV : pct >= 66.67 ? '#1e9e57' : '#c0392b';
  const pctSub = pct !== null ? (pct >= 66.67 ? 'עמד ביעד' : `חסר ${(66.67 - pct).toFixed(1)}%`) : '';
  const workerName = getWorkerName();
  const isAdmin = getWorkerRole() === 'admin';

  const WORK_TILES = [
    { label: 'ניהול בניינים', emoji: '🏢', path: `/buildings/${projectName}` },
    { label: 'דוחות',         emoji: '🗃️', path: `/reports/${projectName}` },
    { label: 'דשבורד',        emoji: '📊', path: `/dashboard/${projectName}` },
    { label: 'כנסים',          emoji: '🎉', path: `/events/${projectName}` },
    ...(decodeURIComponent(projectName) === 'ג החדשה' ? [{ label: 'שיבוצים', emoji: '🏢', path: `/placements/${projectName}` }] : []),
    ...(isTrialUser() ? [{ label: 'כרטיס ביקור דיגיטלי', emoji: '📇', path: `/card-editor/${projectName}` }] : []),
  ];
  const TENANT_TILES = [
    { label: 'דשבורד פעילות', emoji: '📊', path: `/admin/${projectName}?tab=dashboard` },
    { label: 'דף הבית',       emoji: '🏠', path: `/admin/${projectName}?tab=home` },
    { label: 'הדירה שלי',     emoji: '🏡', path: `/admin/${projectName}?tab=apartment` },
    { label: 'שלבים',         emoji: '📋', path: `/admin/${projectName}` },
    { label: 'הודעות',        emoji: '📣', path: `/admin/${projectName}?tab=announcements` },
    { label: 'גלריית תמונות', emoji: '📷', path: `/admin/${projectName}?tab=gallery` },
    { label: 'אנשי קשר',     emoji: '📞', path: `/admin/${projectName}?tab=contacts` },
    { label: 'קבצים',         emoji: '📁', path: `/admin/${projectName}?tab=files` },
    { label: 'אודות היזם',    emoji: 'ℹ️',  path: `/admin/${projectName}?tab=about` },
    ...(isAdmin ? [{ label: 'מעקב צפייה בדייר', emoji: '🔍', path: `/admin/${projectName}?tab=tenant-lookup` }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f3ee', fontFamily: GV_F, direction: 'rtl' }}>

      {/* Header */}
      <div style={{ background: '#1F5C33', padding: '14px 24px', position: 'relative', minHeight: 56, display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 24, fontSize: 15, color: 'rgba(255,255,255,0.95)', cursor: 'pointer', fontFamily: GV_F }} onClick={() => navigate('/projects')}>חזרה לפרויקטים ←</span>
      </div>

      {/* Project logo — big, centered */}
      <div style={{ textAlign: 'center', margin: '18px 0 12px' }}>
        <div style={{ display: 'inline-block', background: 'white', borderRadius: 16, padding: isMobile ? '12px 20px' : '16px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <img src={name === 'אייזיקחריף' ? '/logos/aizik_harif.png' : '/logos/jaffag.png'} alt={name} style={{ maxHeight: isMobile ? 100 : 140, maxWidth: '100%', objectFit: 'contain' }} onError={e => e.target.parentElement.style.display='none'} />
        </div>
      </div>

      {/* 3-column grid on desktop, stacked single column on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '240px 1fr 240px',
        gap: isMobile ? 16 : 24,
        padding: isMobile ? '0 12px 32px' : '0 28px 40px',
        maxWidth: 1300, margin: '0 auto', alignItems: 'stretch',
        boxSizing: 'border-box',
      }}>

        {/* DOM first → visual RIGHT in RTL on desktop; moved to first on mobile too — תמונת מצב */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, border: '2px solid #1F5C33', borderRadius: 16, padding: 14, order: isMobile ? 1 : 0 }}>
          <div style={{ font: `700 14px ${GV_F}`, color: '#1c2530', marginBottom: 2 }}>תמונת מצב</div>
          {stats ? (<>
            <GavsoStat value={stats.totalBuildings} label="בניינים" bg="#C8642C" numColor="#1D252F" labelColor="#1c2530" border="#1F5C33" flex={1} />
            <GavsoStat value={stats.totalApartments} label="דירות" bg="#C8642C" numColor="#1D252F" labelColor="#1c2530" border="#1F5C33" flex={1} />
            <GavsoStat value={stats.totalTenants} label="בעלי זכויות (בשלמות או חלקי)" bg="#C8642C" numColor="#1D252F" labelColor="#1c2530" border="#1F5C33" flex={1} />
            {stats.publicHousingCount > 0 && <GavsoStat value={stats.publicHousingCount} label="יחידת דיור ציבורי" bg="#C8642C" numColor="#1D252F" labelColor="#1c2530" border="#1F5C33" flex={1} />}
          </>) : <div style={{ font: `11px ${GV_F}`, color: '#9aa0af', textAlign: 'center' }}>טוען...</div>}
        </div>

        {/* CENTER — tiles */}
        <div style={{ order: isMobile ? 0 : 1 }}>
          {MsgBadge && <MsgBadge bg="#f0fdf4" textColor="#14532d" borderColor="#1F5C33" />}
          {[
            { label: 'פורטל עבודה', tiles: WORK_TILES, sectionColor: '#1F5C33', tileBg: '#6FAE4E', iconBg: '#ffffff', iconColor: '#1F5C33' },
            { label: 'פורטל דיירים', tiles: TENANT_TILES, sectionColor: '#1F5C33', tileBg: '#0DD03E', iconBg: '#ffffff', iconColor: '#1F5C33' },
          ].map(section => (
            <div key={section.label} style={{ marginBottom: 20 }}>
              <div style={{ font: `700 15px ${GV_F}`, color: section.sectionColor, marginBottom: 10, borderRight: `3px solid ${section.sectionColor}`, paddingRight: 8 }}>
                {section.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14 }}>
                {section.tiles.map(t => (
                  <GavsoTile key={t.label} emoji={t.emoji} label={t.label} onClick={() => navigate(t.path)}
                    tileBg={section.tileBg} iconBg={section.iconBg} iconColor={section.iconColor} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* DOM third → visual LEFT in RTL on desktop; last on mobile too — חתימות ומעקב */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, border: '2px solid #1F5C33', borderRadius: 16, padding: 14, order: 2 }}>
          <div style={{ font: `700 14px ${GV_F}`, color: '#1c2530', marginBottom: 2 }}>חתימות ומעקב</div>
          {stats ? (<>
            <GavsoStat value={stats.totalSigned != null ? Number(stats.totalSigned).toFixed(0) : '—'} label={`חתומים + דיור ציבורי מתוך ${stats.totalApartments} דירות`} bg="#3D7A42" numColor="#F5EFE0" labelColor="#F5EFE0" border="#1F5C33" flex={1} />
            <GavsoStat value={pct !== null ? `${pct.toFixed(1)}%` : '—'} label={pct !== null ? (pct >= 66.67 ? 'עמד ביעד' : `חסר ${(66.67 - pct).toFixed(1)}%`) : 'אחוז חתימות'} bg="#3D7A42" numColor="#F5EFE0" labelColor="#F5EFE0" border="#1F5C33" flex={1} />
            <GavsoStat value={stats.overdueFollowUps > 0 ? stats.overdueFollowUps : '0'} label="מעקבים לטיפול" sub="תאריך מעקב שעבר" bg="#3D7A42" numColor="#F5EFE0" labelColor="#F5EFE0" border="#1F5C33" flex={1} />
          </>) : <div style={{ font: `11px ${GV_F}`, color: '#9aa0af', textAlign: 'center' }}>טוען...</div>}
        </div>

      </div>

      <FloatingAIButton projectName={projectName} navigate={navigate} />
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function ProjectHub() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const name = decodeURIComponent(projectName);
  const [stats, setStats] = useState(null);
  const [tenantMsgs, setTenantMsgs] = useState([]);

  useEffect(() => {
    api.get(`/reports/dashboard?projectName=${encodeURIComponent(name)}`)
      .then(r => setStats(r.data))
      .catch(() => {});
    api.get(`/portal/tenant-messages/${encodeURIComponent(name)}`)
      .then(r => setTenantMsgs(r.data || []))
      .catch(() => {});
  }, [name]);

  const unreadCount = tenantMsgs.filter(m => !m.is_read).length;

  const MsgBadge = ({ bg = '#1a4a8a', textColor = 'white', borderColor = '#1a4a8a' }) => unreadCount > 0 ? (
    <div onClick={() => navigate(`/admin/${projectName}?tab=dashboard`)}
      style={{ cursor: 'pointer', background: bg, border: `2px solid ${borderColor}`, borderRadius: 14, padding: '12px 20px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.4rem' }}>✉️</span>
        <span style={{ fontWeight: 700, color: textColor, fontSize: '0.95rem' }}>
          יש לך {unreadCount} הודע{unreadCount === 1 ? 'ה' : 'ות'} חדש{unreadCount === 1 ? 'ה' : 'ות'} מדיירים
        </span>
      </div>
      <span style={{ background: 'white', color: borderColor, borderRadius: 20, padding: '4px 14px', fontWeight: 700, fontSize: '0.85rem' }}>
        צפה ←
      </span>
    </div>
  ) : null;

  if (AURA_PROJECTS.has(name)) {
    return <AuraHub name={name} projectName={projectName} stats={stats} navigate={navigate} MsgBadge={MsgBadge} />;
  }

  if (GAVSO_PROJECTS.has(name)) {
    return <GavsoHub name={name} projectName={projectName} stats={stats} navigate={navigate} MsgBadge={MsgBadge} />;
  }

  // ── Default layout for non-Aura projects ──
  const isMobile = useIsMobile();
  const workerRole = getWorkerRole();
  const isPM = workerRole === 'project_manager';
  const displayLogo = isPM ? (CLIENT_LOGOS[name] || PROJECT_LOGOS[name]) : PROJECT_LOGOS[name];
  const pct = stats ? parseFloat(stats.percentage) : null;
  const pctColor = pct === null ? '#6b7280' : pct >= 66.67 ? '#16a34a' : pct >= 50 ? '#ca8a04' : '#dc2626';

  const WORK_TILES = [
    { label: 'ניהול בניינים', emoji: '🏢', path: `/buildings/${projectName}` },
    { label: 'דוחות',         emoji: '🗃️', path: `/reports/${projectName}` },
    { label: 'דשבורד',        emoji: '📊', path: `/dashboard/${projectName}` },
    { label: 'כנסים',          emoji: '🎉', path: `/events/${projectName}` },
    ...(decodeURIComponent(projectName) === 'ג החדשה' ? [{ label: 'שיבוצים', emoji: '🏢', path: `/placements/${projectName}` }] : []),
    ...(isTrialUser() ? [{ label: 'כרטיס ביקור דיגיטלי', emoji: '📇', path: `/card-editor/${projectName}` }] : []),
  ];

  const TENANT_TILES = [
    { label: 'דשבורד פעילות', emoji: '📊', path: `/admin/${projectName}?tab=dashboard` },
    { label: 'שלבים',         emoji: '📋', path: `/admin/${projectName}` },
    { label: 'הודעות',        emoji: '📢', path: `/admin/${projectName}?tab=announcements` },
    { label: 'גלריית תמונות', emoji: '📷', path: `/admin/${projectName}?tab=gallery` },
    { label: 'אנשי קשר',     emoji: '📞', path: `/admin/${projectName}?tab=contacts` },
    { label: 'קבצים',         emoji: '📁', path: `/admin/${projectName}?tab=files` },
    { label: 'משימות',        emoji: '✅', path: `/admin/${projectName}?tab=tasks` },
    { label: 'אודות היזם',    emoji: 'ℹ️',  path: `/admin/${projectName}?tab=about` },
    { label: 'דף הבית',       emoji: '🏠', path: `/admin/${projectName}?tab=home` },
    ...(!isPM ? [{ label: 'מעקב צפייה בדייר', emoji: '🔍', path: `/admin/${projectName}?tab=tenant-lookup` }] : []),
  ];

  return (
    <div>
      <Navbar title={displayProjectName(name)} />

      <FloatingAIButton projectName={projectName} navigate={navigate} />

      <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '0 12px' : undefined, boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'left', marginBottom: 12 }}>
          <button onClick={() => navigate('/projects')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', padding: 0 }}>
            ← חזרה לפרויקטים
          </button>
        </div>

        {displayLogo && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <img src={displayLogo} alt={name} style={{ maxHeight: isMobile ? 120 : (isPM ? 220 : 170), maxWidth: '100%', objectFit: 'contain', background: isPM ? 'white' : 'none', borderRadius: isPM ? 12 : 0, padding: isPM ? '12px 24px' : 0, boxShadow: isPM ? '0 2px 12px rgba(0,0,0,0.08)' : 'none' }} />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px 1fr 200px', gap: isMobile ? 16 : 20, alignItems: 'stretch' }}>
          <div style={{ order: isMobile ? 0 : 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <MsgBadge />
            {[
              { label: 'פורטל עבודה', tiles: WORK_TILES, color: '#1a4a8a' },
              { label: 'פורטל דיירים', tiles: TENANT_TILES, color: '#7c3aed' },
            ].map(section => (
              <div key={section.label}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: section.color, marginBottom: 10, paddingRight: 4, borderRight: `3px solid ${section.color}`, paddingLeft: 8 }}>
                  {section.label}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 10 : 14 }}>
                  {section.tiles.map(tile => (
                    <button key={tile.label} onClick={() => navigate(tile.path)}
                      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '22px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>
                      <span style={{ fontSize: '2rem' }}>{tile.emoji}</span>
                      <span>{tile.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 14, padding: '16px 14px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', order: isMobile ? 1 : 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1f2937', marginBottom: 12, textAlign: 'center' }}>📍 תמונת מצב</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {stats ? (
                <>
                  <StatBox label="בניינים"     value={stats.totalBuildings}      color="#1a4a8a" flex={1} />
                  <StatBox label="דירות"       value={stats.totalApartments}     color="#1a4a8a" flex={1} />
                  <StatBox label="בעלי זכויות (בשלמות או חלקי)" value={stats.totalTenants} color="#1a4a8a" flex={1} />
                  {stats.publicHousingCount > 0 && <StatBox label="יחידת דיור ציבורי" value={stats.publicHousingCount} color="#7c3aed" flex={1} />}
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center' }}>טוען...</div>
              )}
            </div>
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 14, padding: '16px 14px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', order: 2 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1f2937', marginBottom: 12, textAlign: 'center' }}>✍️ חתימות ומעקב</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {stats ? (
                <>
                  <StatBox label="חתומים + דיור ציבורי" value={stats.totalSigned != null ? Number(stats.totalSigned).toFixed(0) : '—'} color="#16a34a" sub={`מתוך ${stats.totalApartments} דירות`} flex={1} />
                  <StatBox label="אחוז חתימות" value={pct !== null ? `${pct.toFixed(1)}%` : '—'} color={pctColor} sub={pct !== null ? (pct >= 66.67 ? 'עמד ביעד' : `חסר ${(66.67 - pct).toFixed(1)}%`) : ''} flex={1} />
                  <StatBox label="מעקבים לטיפול" value={stats.overdueFollowUps > 0 ? stats.overdueFollowUps : '0'} color={stats.overdueFollowUps > 0 ? '#dc2626' : '#16a34a'} sub="תאריך מעקב שעבר" flex={1} />
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center' }}>טוען...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
